/**
 * Prices by the glass (docs/15-getraenkekarte.md, Offene Weine): a drink served open has one or
 * more serving sizes, each with its price. Written as people write it on a card:
 * "1/8 6,50; 1/4 12,50", "0,1 l 7,50", "5 cl 6,50", "Schoppen 6,50". The names of the sizes come
 * from the measures (measures.ts); without a context they are those of the wine profile.
 */
import { measureName, parseMeasure } from "./measures.ts";
import type { MeasureContext } from "./measures.ts";
import { WINE_MEASURES } from "./profile-wine.ts";

export interface GlassPrice {
  /** Serving size in ml. */
  ml: number;
  /** Cents. */
  price: number;
}

const WINE: MeasureContext = { profile: WINE_MEASURES };

/** "1/8 l", "0,1 l", "5 cl": how a serving size is written on a card. */
export function glassSize(ml: number, ctx: MeasureContext = WINE): string {
  return measureName(ml, "glass", ctx);
}

const euro = (cents: number) => (cents / 100).toFixed(2).replace(".", ",");

/** The prices as they are typed and shown: "1/8 l 6,50; 1/4 l 12,50". */
export function formatGlasses(glasses: readonly GlassPrice[], ctx: MeasureContext = WINE): string {
  return glasses.map((g) => `${glassSize(g.ml, ctx)} ${euro(g.price)}`).join("; ");
}

/** Reads "1/8 6,50; 1/4 12,50"; a size in any form parseMeasure reads. Sorted by size. */
export function parseGlasses(text: string, ctx: MeasureContext = WINE): { value: GlassPrice[] } | { error: string } {
  const glasses: GlassPrice[] = [];
  for (const part of text.split(/[;|\n]+/).map((s) => s.trim()).filter(Boolean)) {
    const m = /^(.*?)\s*(?:[:=à@-]|zu)?\s*(?:€\s*)?(\d+(?:[.,]\d{1,2})?)\s*€?$/i.exec(part);
    if (!m || m[1]!.trim() === "") return { error: `„${part}“: bitte Menge und Preis, z. B. „1/8 6,50“ oder „5 cl 6,50“` };
    const ml = parseMeasure(m[1]!, ctx);
    if (ml === null || !(ml <= 1500)) return { error: `„${part}“: Menge unbekannt` };
    glasses.push({ ml, price: Math.round(Number(m[2]!.replace(",", ".")) * 100) });
  }
  return { value: glasses.sort((a, b) => a.ml - b.ml) };
}
